// src/components/FdwMcpLogin.tsx
//
// Login page for the FDW MCP OAuth flow.
// Served at fivedollarworkday.com/mcp/login
// Route: /mcp/login?session={sessionId}
//
// Flow:
//   1. User lands here from /mcp/auth redirect (Claude initiated OAuth)
//   2. User signs in with Google via Firebase Auth
//   3. React POSTs the Firebase ID token to /mcp/firebase-verify
//   4. On success, redirects to /mcp/auth/callback?session={sessionId}
//   5. Callback handler issues auth code and redirects back to Claude
//
// Same pattern as AGC's mcp.login.tsx — different domain, FDW identity.

import React, { useState, useEffect } from 'react';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { app } from '../services/firebase';

const auth     = getAuth(app);
const provider = new GoogleAuthProvider();

export const FdwMcpLogin: React.FC = () => {
  const [status,  setStatus]  = useState<'idle' | 'signing_in' | 'verifying' | 'redirecting' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const sessionId = new URLSearchParams(window.location.search).get('session');
  const errorParam = new URLSearchParams(window.location.search).get('error');

  useEffect(() => {
    if (errorParam) setMessage(decodeURIComponent(errorParam));
  }, [errorParam]);

  useEffect(() => {
    // If user is already signed in when they land here, verify immediately.
    const unsub = onAuthStateChanged(auth, user => {
      if (user && sessionId && status === 'idle') {
        verify(user);
      }
    });
    return () => unsub();
  }, [sessionId, status]);

  async function verify(user: User) {
    if (!sessionId) {
      setStatus('error');
      setMessage('Missing session. Please try connecting again from Claude.');
      return;
    }

    try {
      setStatus('verifying');
      const idToken = await user.getIdToken();

      const res = await fetch('/mcp/firebase-verify', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ idToken, sessionId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Verification failed');
      }

      setStatus('redirecting');
      window.location.href = `/mcp/auth/callback?session=${sessionId}`;

    } catch (e: any) {
      console.error('[FdwMcpLogin] verify error:', e);
      setStatus('error');
      setMessage(e.message ?? 'Something went wrong. Please try again.');
    }
  }

  async function signIn() {
    if (!sessionId) {
      setStatus('error');
      setMessage('Missing session. Please try connecting again from Claude.');
      return;
    }

    try {
      setStatus('signing_in');
      const result = await signInWithPopup(auth, provider);
      await verify(result.user);
    } catch (e: any) {
      console.error('[FdwMcpLogin] signIn error:', e);
      if (e.code === 'auth/popup-closed-by-user') {
        setStatus('idle');
        return;
      }
      setStatus('error');
      setMessage(e.message ?? 'Sign-in failed. Please try again.');
    }
  }

  const isLoading = status === 'signing_in' || status === 'verifying' || status === 'redirecting';

  const statusLabel = {
    idle:        null,
    signing_in:  'Signing in…',
    verifying:   'Verifying…',
    redirecting: 'Returning to Claude…',
    error:       null,
  }[status];

  return (
    <div className="min-h-screen bg-brand-white flex items-center justify-center px-6">
      <div className="max-w-sm w-full">

        <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
          FDW · MCP Connect
        </p>

        <h1 className="text-2xl font-semibold text-brand-black mb-3 tracking-tight">
          Connect to Claude
        </h1>

        <p className="text-sm text-brand-charcoal font-light leading-relaxed mb-10">
          Sign in to establish your agentic presence. Claude will walk you through the rest.
        </p>

        {message && (
          <div className="mb-6 px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700 font-light">
            {message}
          </div>
        )}

        {!sessionId && (
          <div className="mb-6 px-4 py-3 rounded-xl border border-brand-border bg-brand-surface text-sm text-brand-charcoal font-light">
            No session found. Return to Claude and paste the FDW MCP URL to start a new connection.
          </div>
        )}

        <button
          onClick={signIn}
          disabled={isLoading || !sessionId}
          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-brand-black text-white rounded-full hover:bg-brand-charcoal transition-all text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {statusLabel}
            </>
          ) : (
            <>
              <GoogleIcon />
              Continue with Google
            </>
          )}
        </button>

        <p className="mt-6 text-xs font-mono text-brand-gray text-center">
          fivedollarworkday.com · MCP connector
        </p>

      </div>
    </div>
  );
};

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M15.68 8.18c0-.57-.05-1.12-.14-1.64H8v3.1h4.3a3.67 3.67 0 0 1-1.6 2.41v2h2.58c1.51-1.39 2.4-3.44 2.4-5.87z" fill="#4285F4"/>
      <path d="M8 16c2.16 0 3.97-.71 5.3-1.93l-2.58-2a4.8 4.8 0 0 1-7.15-2.52H.96v2.07A8 8 0 0 0 8 16z" fill="#34A853"/>
      <path d="M3.57 9.55A4.8 4.8 0 0 1 3.32 8c0-.54.09-1.06.25-1.55V4.38H.96A8 8 0 0 0 0 8c0 1.29.31 2.51.96 3.62l2.61-2.07z" fill="#FBBC05"/>
      <path d="M8 3.18c1.22 0 2.31.42 3.17 1.24l2.37-2.37A8 8 0 0 0 .96 4.38l2.61 2.07A4.77 4.77 0 0 1 8 3.18z" fill="#EA4335"/>
    </svg>
  );
}